import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Tooltip, Button } from 'antd';

import {
  selectors,
  fetchBlueprint,
  retrieveSession,
  resetActiveSession,
  fetchKlasses,
  fetchHistoricalStep,
  fetchMarketHours,
  fetchBenchmarkCoverageDiagnostics,
  BlueprintVersion,
  BlueprintVariant,
} from 'state';
import { LoadingScreen } from 'components';

import { TrainerSessionContextWrapper } from '../../../TrainerSession/TrainerSessionContext';
import BenchmarkCoverageDiagnosticsDrawer from '../../Sessions/Session/BenchmarkCoverageDiagnosticsDrawer';
import BlueprintSettingsV2 from './BlueprintSettingsV2';
import useAnswerKeyCoverage from '../useAnswerKeyCoverage';
import AssessmentRegenerationStatus from '../AssessmentRegenerationStatus';
// Native definition controls share authoring while their runtime remains separately owned.
import NativeDefinitionCard from '../../../NativeSession/NativeDefinitionCard';

/**
 * Isolated V2 copy of blueprint authoring. It retains the same template-session
 * loading and cleanup contract as V1, but owns a blueprint-specific settings
 * shell so layout work cannot change trainer settings or V1 authoring.
 */
const BlueprintAuthoringV2: React.FC = () => {
  const dispatch = useDispatch();
  const { blueprintId, variantId } = useParams<{
    blueprintId: string;
    variantId: string;
  }>();
  const familyId = Number(blueprintId);
  const variantIdNum = Number(variantId);

  const blueprints = useSelector(selectors.blueprints);
  const blueprintsWaiting = useSelector(selectors.blueprintsWaiting);
  const blueprint = blueprints[familyId];

  let variant: BlueprintVariant | undefined;
  let version: BlueprintVersion | undefined;
  if (blueprint) {
    for (const candidate of blueprint.versions) {
      const found = candidate.variants.find(item => item.id === variantIdNum);
      if (found) {
        variant = found;
        version = candidate;
        break;
      }
    }
  }

  const templateSessionId = variant?.template_session;
  const isLocked = version?.is_locked ?? false;
  const session = useSelector(selectors.activeSession);
  const assessmentRefreshToken = useMemo(
    () => ({ session, blueprint }),
    [session, blueprint],
  );
  const assessment = useAnswerKeyCoverage({
    blueprintId: familyId,
    variantId: variant?.id,
    refreshToken: assessmentRefreshToken,
    statusOnly: true,
  });

  const [fetchAttempted, setFetchAttempted] = useState(false);
  useEffect(() => {
    if (!blueprint) {
      setFetchAttempted(true);
      dispatch(fetchBlueprint({ id: familyId, summary_scope: 'none' }));
    }
  }, [dispatch, blueprint, familyId]);

  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  useEffect(() => {
    if (!diagnosticsOpen || !templateSessionId) return;
    dispatch(
      fetchBenchmarkCoverageDiagnostics({ session_id: templateSessionId }),
    );
  }, [diagnosticsOpen, dispatch, templateSessionId]);

  useEffect(() => {
    if (!templateSessionId) return;
    dispatch(retrieveSession({ id: templateSessionId }));
    dispatch(fetchKlasses({}));
    dispatch(fetchHistoricalStep({}));
    dispatch(fetchMarketHours({}));
    return () => {
      dispatch(resetActiveSession({}));
    };
  }, [dispatch, templateSessionId]);

  if (!blueprint && (blueprintsWaiting || !fetchAttempted)) {
    return <LoadingScreen fullscreen />;
  }

  if (!blueprint || !variant || !templateSessionId) {
    return (
      <div>
        <Link to={`/dashboard/blueprints/${familyId}`}>
          ← Back to blueprint
        </Link>
        <Empty style={{ marginTop: 40 }} description="Variant not found" />
      </div>
    );
  }

  if (!session || session.id !== templateSessionId) {
    return <LoadingScreen fullscreen />;
  }

  return (
    <TrainerSessionContextWrapper>
      <>
        {session.execution_engine === 'fast' && <NativeDefinitionCard sessionId={templateSessionId}
          mode={session.fast_advancement_mode || 'daily'} readOnly={isLocked} />}
        <BlueprintSettingsV2
          blueprintId={familyId}
          blueprintName={blueprint.name}
          difficultyLabel={variant.difficulty.label}
          versionNumber={version?.version_number}
          sessionId={templateSessionId}
          variantId={variantIdNum}
          readOnly={isLocked}
          onOpenDiagnostics={() => setDiagnosticsOpen(true)}
          assessmentStatus={
            assessment.error ? (
              <Tooltip title={assessment.error}>
                <Button size="small" onClick={assessment.refresh}>
                  Assessment status unavailable · Retry
                </Button>
              </Tooltip>
            ) : (
              <AssessmentRegenerationStatus
                compact
                regeneration={assessment.document?.regeneration}
                onRetry={assessment.retry}
                retrying={assessment.retrying}
              />
            )
          }
        />
        <BenchmarkCoverageDiagnosticsDrawer
          open={diagnosticsOpen}
          onClose={() => setDiagnosticsOpen(false)}
          sessionId={templateSessionId}
        />
      </>
    </TrainerSessionContextWrapper>
  );
};

export default BlueprintAuthoringV2;
